import React, { useState, useCallback } from "react";
import { useApi } from "../../api/useApi";
import { availableWorkGroups } from "../../api/workgroupQueries";
import { StyledSelect, FormControl } from "../../components/Form/FormControls";
import ErrorMessages from "../../components/Notifications/ErrorMessages";
import DataTable from "../../components/Table/DataTable";
import { useSearch } from "../../Hooks/useSearch";
import SearchInput from "../../components/Forms/Search/SearchInput";

// ENUM for permissions
// None = 0,
// Read = 1,
// Write = 2,
// Execute = 4,
// Grant = 8
const permissionRoles = [
  { label: "Full", value: 15 },
  { label: "Elevated", value: 7 },
  { label: "Read Only", value: 1 },
  { label: "None", value: 0 },
];

const StaticGroupSelect = ({ props }) => {
  // `rowsPerPage`/`maxHeight` let a caller embedding this in a form keep the grid
  // short. The permissions screens leave them alone and get the full-size grid.
  const { form, field, rowsPerPage = 50, maxHeight } = props;

  const permissions = form.values.permissions;

  const setUpPermissions = (permissionUpdate) => {
    const { value, groupId } = permissionUpdate;
    const existingPermissions = [...permissions];
    const existingIndex = permissions.findIndex(
      (perm) => perm.accessingWorkGroupId === groupId
    );

    if (existingIndex === -1) {
      existingPermissions.push({
        accessingWorkGroupId: groupId,
        permissionCode: value.value,
      });
    } else {
      existingPermissions[existingIndex].permissionCode = value.value;
    }
    return existingPermissions;
  };

  const updateField = (permissionUpdate) => {
    const permissionsUpdated = setUpPermissions(permissionUpdate);
    form.setFieldValue(field.name, permissionsUpdated);
  };

  const columnsData = [
    {
      Header: "Permission Groups",
      id: "group",
      accessor: (d) => d?.node?.displayName,
    },
    {
      Header: "Access",
      id: "access",
      accessor: (d) => {
        const foundPermissions = permissions.find(
          (perm) => perm.accessingWorkGroupId === d?.node?.id
        );

        return foundPermissions ? foundPermissions.permissionCode : null;
      },
      Cell: ({ row: { original } }) => {
        const foundPermissions = permissions.find(
          (perm) => perm.accessingWorkGroupId === original?.node?.id
        );

        let selectedRole;
        if (foundPermissions) {
          selectedRole = permissionRoles.find(
            (pr) => pr.value === foundPermissions.permissionCode
          );
        } else {
          selectedRole = null;
        }

        return (
          <StyledSelect
            id={`${original?.node?.displayName}`}
            inputId={`${original?.node?.displayName}-PermissionSelect`}
            instanceId={`${original?.node?.displayName}-PermissionSelect-instance`}
            className={`react-select-container`}
            classNamePrefix={`react-select`}
            name={`permission`}
            menuPortalTarget={document.body}
            placeholder={`Permission`}
            label="Permission"
            value={selectedRole}
            options={permissionRoles}
            onChange={(e) =>
              updateField({ value: e, groupId: original?.node?.id })
            }
          />
        );
      },
    },
  ];

  const [{ errors, loading, data }, doFetch] = useApi(availableWorkGroups);
  const [pageSize, setPageSize] = useState(rowsPerPage);

  const groups = data?.availableWorkGroups?.edges ?? [];
  const totalCount = data?.availableWorkGroups?.totalCount;
  const pageInfo = data?.availableWorkGroups?.pageInfo;

  const workgroups = groups.sort(function (a, b) {
    if (a.type > b.type) {
      return -1;
    }
    if (a.type < b.type) {
      return 1;
    }
    return 0;
  });

  const enabledWorkgroups = workgroups.filter(({ node }) => node?.enabled);

  //Fetch for Table Paged
  const fetchData = useCallback(
    ({ pageSize: incomingPageSize, cursor }) => {
      const finalPageSize = incomingPageSize || pageSize;
      doFetch({
        query: availableWorkGroups,
        variables: {
          first: finalPageSize,
          after: cursor,
          order: { displayName: "ASC" },
        },
      });
    },
    [doFetch, pageSize]
  );

  const onSearch = useCallback(
    (text) => {
      const where = {};
      if (text) {
        where.or = [{ displayName: { contains: text } }];
      }

      const variables = {
        order: [{ displayName: "ASC" }],
        ...(Object.keys(where).length > 0 && { where }),
        first: pageSize, // Always use current page size
      };

      doFetch({
        query: availableWorkGroups,
        variables,
      });
    },
    [doFetch, pageSize]
  );

  const { inputText, handleInputChange, tooShortWarning } = useSearch({
    onSearch,
  });

  return (
    <>
      <FormControl style={{ maxWidth: "300px" }}>
        <SearchInput
          label="Search Groups"
          placeholder="Search Groups"
          inputText={inputText}
          handleChange={handleInputChange}
          warning={tooShortWarning}
        />

        {errors ? <ErrorMessages errors={errors} /> : null}
      </FormControl>

      <DataTable
        // Server query has no sort wiring, so a clickable header would
        // paint a sort arrow without reordering rows (AC-15839).
        disableSortBy
        fetchData={fetchData}
        loading={loading}
        pageInfo={pageInfo}
        totalCount={totalCount}
        data={enabledWorkgroups}
        columns={columnsData}
        defaultPageSize={rowsPerPage}
        maxHeight={maxHeight}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
      />
    </>
  );
};

export default React.memo((props) => {
  return (
    <>
      <StaticGroupSelect props={props} />
    </>
  );
});
